import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BatteryMedium, ShieldCheck, Tag, ChevronRight, Sparkles, SlidersHorizontal } from "lucide-react";
import { fetchUsato, euro, USED_STATUS_META, mediaUrl } from "@/lib/store";

const GradeBadge = ({ g }) => g ? (
  <span className="inline-flex items-center rounded-md bg-neutral-900 px-2 py-0.5 text-xs font-bold text-white">Grado {g}</span>
) : null;

// "Appena arrivato": inserito negli ultimi 7 giorni
const isNew = (iso) => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !isNaN(t) && (Date.now() - t) < 7 * 24 * 3600 * 1000;
};

export default function Usato() {
  const [items, setItems] = useState(null);
  const [marca, setMarca] = useState("");
  const [grado, setGrado] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("recenti");

  useEffect(() => { fetchUsato().then(setItems).catch(() => setItems([])); }, []);

  const marche = useMemo(() => Array.from(new Set((items || []).map((d) => d.marca).filter(Boolean))).sort(), [items]);
  const gradi = useMemo(() => Array.from(new Set((items || []).map((d) => d.grado).filter(Boolean))).sort(), [items]);

  const filtered = useMemo(() => {
    let list = (items || []).filter((d) => {
      if (marca && d.marca !== marca) return false;
      if (grado && d.grado !== grado) return false;
      if (maxPrice && (d.prezzo_vendita_cents || 0) > Math.round(parseFloat(maxPrice) * 100)) return false;
      return true;
    });
    if (sort === "prezzo_asc") list = [...list].sort((a, b) => (a.prezzo_vendita_cents || 0) - (b.prezzo_vendita_cents || 0));
    else if (sort === "prezzo_desc") list = [...list].sort((a, b) => (b.prezzo_vendita_cents || 0) - (a.prezzo_vendita_cents || 0));
    else list = [...list].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    return list;
  }, [items, marca, grado, maxPrice, sort]);

  const resetFilters = () => { setMarca(""); setGrado(""); setMaxPrice(""); setSort("recenti"); };

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14" data-testid="usato-page">
      <Helmet><title>Usato IPHIX — iPhone e dispositivi ricondizionati | iPhix Scafati</title></Helmet>
      <div className="mb-8">
        <p className="mb-2 font-mono text-xs uppercase tracking-widest text-neutral-400">Usato IPHIX</p>
        <h1 className="text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl">Usato garantito del negozio</h1>
        <p className="mt-3 max-w-2xl text-base text-neutral-600">Dispositivi reali testati in negozio: foto autentiche dell'unità, condizioni estetiche, batteria e garanzia. Prenota o richiedi disponibilità su WhatsApp.</p>
      </div>

      {/* Filtri */}
      {items && items.length > 0 && (
        <div data-testid="usato-filters" className="mb-6 flex flex-wrap items-end gap-3 rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-neutral-700"><SlidersHorizontal className="h-4 w-4" /> Filtra</div>
          <label className="flex flex-col text-xs text-neutral-500">Marca
            <select data-testid="usato-filter-marca" value={marca} onChange={(e) => setMarca(e.target.value)} className="mt-1 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900">
              <option value="">Tutte</option>{marche.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label className="flex flex-col text-xs text-neutral-500">Grado
            <select data-testid="usato-filter-grado" value={grado} onChange={(e) => setGrado(e.target.value)} className="mt-1 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900">
              <option value="">Tutti</option>{gradi.map((g) => <option key={g} value={g}>Grado {g}</option>)}
            </select>
          </label>
          <label className="flex flex-col text-xs text-neutral-500">Prezzo max (€)
            <input data-testid="usato-filter-prezzo" type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="es. 300" className="mt-1 w-28 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900" />
          </label>
          <label className="flex flex-col text-xs text-neutral-500">Ordina
            <select data-testid="usato-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="mt-1 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900">
              <option value="recenti">Più recenti</option>
              <option value="prezzo_asc">Prezzo crescente</option>
              <option value="prezzo_desc">Prezzo decrescente</option>
            </select>
          </label>
          {(marca || grado || maxPrice || sort !== "recenti") && (
            <button data-testid="usato-filter-reset" onClick={resetFilters} className="ml-auto rounded-full border border-neutral-300 px-3 py-2 text-xs font-medium text-neutral-600 hover:border-neutral-900 hover:text-neutral-900">Azzera filtri</button>
          )}
        </div>
      )}

      {items === null ? (
        <p className="text-neutral-400">Caricamento…</p>
      ) : items.length === 0 ? (
        <div data-testid="usato-empty" className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-12 text-center text-neutral-500">
          Al momento non ci sono dispositivi usati disponibili. Scrivici su WhatsApp per le ultime entrate.
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="usato-nofilter" className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-12 text-center text-neutral-500">
          Nessun dispositivo corrisponde ai filtri selezionati. <button onClick={resetFilters} className="underline">Azzera i filtri</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="usato-grid">
          {filtered.map((d) => {
            const meta = USED_STATUS_META[d.stato] || {};
            return (
              <Link key={d.id} to={`/usato/${d.slug || d.id}`} data-testid={`usato-card-${d.id}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-neutral-900 hover:shadow-xl">
                <div className="relative aspect-square overflow-hidden bg-neutral-100">
                  {d.image ? (
                    <img src={mediaUrl(d.image)} alt={`${d.marca} ${d.modello}`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-neutral-300">Nessuna foto</div>
                  )}
                  <span className={`absolute left-3 top-3 rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.pub || "bg-white text-neutral-700 border-neutral-200"}`}>{meta.label || d.stato}</span>
                  {isNew(d.created_at) && (
                    <span data-testid={`usato-new-${d.id}`} className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-neutral-900 px-2.5 py-1 text-xs font-bold text-white shadow"><Sparkles className="h-3 w-3" /> Appena arrivato</span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-neutral-900">{d.marca} {d.modello}</h3>
                    <GradeBadge g={d.grado} />
                  </div>
                  <p className="text-sm text-neutral-500">{[d.memoria, d.colore].filter(Boolean).join(" · ")}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {d.is_apple && <span className="rounded-full bg-neutral-900 px-2 py-0.5 text-[10px] font-bold text-white">100% Originale Apple</span>}
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Testato</span>
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">Garanzia {d.warranty_months || 12} mesi</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-3 text-xs text-neutral-500">
                    {d.batteria_pct != null && <span className="inline-flex items-center gap-1"><BatteryMedium className="h-3.5 w-3.5" /> {d.batteria_pct}%</span>}
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-neutral-100 pt-3">
                    {d.promo_attiva ? (
                      <span className="inline-flex items-baseline gap-2"><span className="text-xl font-bold text-rose-600">{euro(d.promo_price_cents)}</span><span className="text-sm text-neutral-400 line-through">{euro(d.prezzo_vendita_cents)}</span></span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xl font-bold text-neutral-900"><Tag className="h-4 w-4 text-emerald-600" />{euro(d.prezzo_vendita_cents)}</span>
                    )}
                    <span className="inline-flex items-center text-sm font-medium text-neutral-500 group-hover:text-neutral-900">Dettagli <ChevronRight className="h-4 w-4" /></span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </main>
  );
}
